import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { SafeUrl } from '@angular/platform-browser';
import { slideIn } from '@core/animations/slide-in.animation';
import { RecoveryCode, User } from '@core/interfaces/users';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { sentry } from 'sentry';
import english from '../../i18n/en.json';
import spanish from '../../i18n/es.json';
import portuguese from '../../i18n/pt.json';
import { MfaService } from '../../services/mfa.service';
import { RecoveryCodesService } from '../shared/recovery-codes/services/recovery-codes.service';

interface ReadRecoveryCodesDialogData {
  me: User;
  step: number;
  type?: string;
  recoveryCodes?: RecoveryCode[];
}
@Component({
  templateUrl: './read-recovery-codes.component.html',
  styleUrls: ['./read-recovery-codes.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [slideIn]
})
export class ReadRecoveryCodesComponent {
  public isLoading: boolean;
  public temporaryToken: string;
  public recoveryCodes: RecoveryCode[];
  public downloadRecoveryCodesUrl: SafeUrl;
  public isRecreatedRecoveryCodes: boolean;
  public configureRecoveryCodesStep: number;
  public nextStepAfterNewTemporaryToken: number;

  public type: string;
  public readonly me: User;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ReadRecoveryCodesDialogData,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    public readonly matDialogRef: MatDialogRef<ReadRecoveryCodesComponent>,
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly recoveryCodesService: RecoveryCodesService,
    private readonly mfaService: MfaService
  ) {
    this.isLoading = false;
    this.recoveryCodes = null;
    this.temporaryToken = null;
    this.downloadRecoveryCodesUrl = null;
    this.isRecreatedRecoveryCodes = false;
    this.nextStepAfterNewTemporaryToken = 1;

    this.me = data.me;
    this.type = data.type;
    this.configureRecoveryCodesStep = data.step;

    this.recoveryCodes = data.recoveryCodes;
    if (this.recoveryCodes) {
      this.createDocumentWithRecoveryCodes();
    }

    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async getRecoveryCodes(accessToken: string): Promise<void> {
    try {
      this.isLoading = true;
      this.temporaryToken = accessToken;
      this.fuseProgressBarService.show();

      this.recoveryCodes = await this.mfaService.getRecoveryCodes({
        accessToken: this.temporaryToken
      });

      this.createDocumentWithRecoveryCodes();
      this.configureRecoveryCodesStep = 1;
    } catch (err) {
      this.ensureTemporaryTokenIsValid(err);
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
    }
  }

  public async recreateRecoveryCodes(accessToken?: string): Promise<void> {
    try {
      if (accessToken) {
        this.temporaryToken = accessToken;
      }

      if (!this.temporaryToken) {
        this.nextStepAfterNewTemporaryToken = 2;
        this.configureRecoveryCodesStep = 0;
        return;
      }

      this.isLoading = true;
      this.fuseProgressBarService.show();

      this.recoveryCodes = await this.mfaService.regenerateRecoveryCodes({
        accessToken: this.temporaryToken
      });

      this.createDocumentWithRecoveryCodes();
      this.configureRecoveryCodesStep = 1;
      this.isRecreatedRecoveryCodes = true;
    } catch (err) {
      this.ensureTemporaryTokenIsValid(err);
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
    }
  }

  private createDocumentWithRecoveryCodes(): void {
    this.downloadRecoveryCodesUrl = this.recoveryCodesService.generateFileToDownload({
      recoveryCodes: this.recoveryCodes,
      userEmail: this.me.email
    });
  }

  private ensureTemporaryTokenIsValid(err: any): void {
    if (['OldToken', 'InvalidToken'].includes(getErrorCode(err))) {
      this.nextStepAfterNewTemporaryToken = this.configureRecoveryCodesStep;
      this.configureRecoveryCodesStep = 0;
      return;
    }

    sentry.captureException(err, {
      extra: {
        message: 'Error on recovery codes',
        err: JSON.stringify(err)
      }
    });
  }
}
